/**
 * User Impersonation API Client
 *
 * Handles user impersonation functionality for Super Admins
 */

export interface ImpersonationSession {
  id: string;
  impersonatorUserId: number;
  targetUserId: number;
  token: string;
  createdAt: string;
  expiresAt: string;
  isActive: boolean;
}

export interface StartImpersonationRequest {
  targetUserId: number;
}

export interface StartImpersonationResponse {
  success: boolean;
  data: {
    sessionId: string;
    token: string;
    expiresAt: string;
  };
}

export interface EndImpersonationRequest {
  sessionId: string;
}

export interface EndImpersonationResponse {
  success: boolean;
  message: string;
}

export interface GetImpersonationSessionsResponse {
  success: boolean;
  data: ImpersonationSession[];
}

/**
 * API Client Configuration
 */
const API_BASE = '/api/admin/impersonation';

/**
 * Get authentication headers
 * Uses impersonation token if available, otherwise falls back to regular token
 */
function getAuthHeaders(): HeadersInit {
  // Try impersonation token first (for when admin is already impersonating)
  const impersonationToken = sessionStorage.getItem('impersonation_token');
  const token = localStorage.getItem('token');
  
  const authToken = impersonationToken || token;
  
  if (!authToken) {
    throw new Error('No authentication token found');
  }
  return {
    'Authorization': `Bearer ${authToken}`,
    'Content-Type': 'application/json',
  };
}

/**
 * Handle API response
 */
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Network error' }));
    throw new Error(errorData.message || `HTTP ${response.status}`);
  }

  return response.json();
}

export class ImpersonationApi {
  /**
   * Start a user impersonation session
   */
  static async startImpersonation(request: StartImpersonationRequest): Promise<StartImpersonationResponse> {
    const response = await fetch(`${API_BASE}/start`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(request),
    });

    return handleResponse<StartImpersonationResponse>(response);
  }

  /**
   * End a user impersonation session
   */
  static async endImpersonation(request: EndImpersonationRequest): Promise<EndImpersonationResponse> {
    const response = await fetch(`${API_BASE}/end`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(request),
    });

    return handleResponse<EndImpersonationResponse>(response);
  }

  /**
   * Get active impersonation sessions for the current user
   */
  static async getImpersonationSessions(): Promise<GetImpersonationSessionsResponse> {
    const response = await fetch(`${API_BASE}/sessions`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    return handleResponse<GetImpersonationSessionsResponse>(response);
  }
}

export const impersonationApi = ImpersonationApi;